import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UI_THEME } from '../ui/theme';
import {
  STATUS_SOLICITACAO_LABEL,
  STATUS_SOLICITACAO_ORDEM,
  rotuloAlvo,
  rotuloCategoria,
  solicitacaoService,
  type FiltroFila,
} from '../services/solicitacaoService';

const TAMANHO = 20;

const formatarData = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

const TOM_STATUS: Record<string, string> = {
  ABERTA: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-700/50 dark:bg-amber-500/10 dark:text-amber-300',
  EM_ANALISE: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-700/50 dark:bg-sky-500/10 dark:text-sky-300',
  ATENDIDA: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-700/50 dark:bg-emerald-500/10 dark:text-emerald-300',
  RECUSADA: 'border-red-200 bg-red-50 text-red-800 dark:border-red-700/50 dark:bg-red-500/10 dark:text-red-300',
};

/** Fila da equipe: todas as solicitações, com filtro por status, solicitante e emenda. */
const FilaSolicitacoes: React.FC = () => {
  const isSaas = UI_THEME === 'saas';
  const [status, setStatus] = useState('');
  const [solicitante, setSolicitante] = useState('');
  const [emenda, setEmenda] = useState('');
  const [aplicado, setAplicado] = useState<FiltroFila>({});
  const [pagina, setPagina] = useState(0);

  const fila = useQuery({
    queryKey: ['solicitacoes', 'fila', aplicado, pagina],
    queryFn: () => solicitacaoService.fila({ ...aplicado, pagina, tamanho: TAMANHO }),
    placeholderData: keepPreviousData,
  });

  const filtrar = (e: React.FormEvent) => {
    e.preventDefault();
    setAplicado({ status, solicitante, emenda });
    setPagina(0);
  };
  const escolherStatus = (st: string) => {
    setStatus(st);
    setAplicado({ status: st, solicitante, emenda });
    setPagina(0);
  };

  const surface = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const accent = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const label = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400';
  const input =
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40';
  const primary = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';

  const outros = useQuery({ queryKey: ['solicitacoes', 'outros'], queryFn: solicitacaoService.outros });

  const totalPaginas = fila.data ? Math.max(1, Math.ceil(fila.data.total / TAMANHO)) : 1;

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {STATUS_SOLICITACAO_ORDEM.map((st) => (
          <button
            key={st}
            type="button"
            onClick={() => escolherStatus(status === st ? '' : st)}
            aria-pressed={status === st}
            className={`rounded-2xl border p-4 text-left transition hover:shadow ${surface} ${
              status === st ? 'ring-2 ring-blue-500' : ''
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              {STATUS_SOLICITACAO_LABEL[st]}
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
              {fila.data?.contagemPorStatus?.[st] ?? '—'}
            </p>
          </button>
        ))}
      </section>

      {outros.data && outros.data.length > 0 && (
        <section className={`rounded-3xl border p-5 ${surface}`} aria-label="Tipos digitados em Outro">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Pedidos em "Outro": o que mais se repete</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Tipos que os parlamentares digitaram quando a categoria não estava na lista. Os que mais aparecem são
            candidatos a virar categoria própria ou entrar no fluxo.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {outros.data.slice(0, 12).map((o) => (
              <li key={o.texto} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {o.texto} <span className="font-bold">({o.quantidade})</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={`rounded-3xl border p-5 ${surface}`}>
        <form onSubmit={filtrar} className="grid grid-cols-1 gap-3 md:grid-cols-[180px_1fr_1fr_auto] md:items-end">
          <div>
            <label htmlFor="fila-status" className={label}>Status</label>
            <select id="fila-status" className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Todos</option>
              {STATUS_SOLICITACAO_ORDEM.map((st) => (
                <option key={st} value={st}>{STATUS_SOLICITACAO_LABEL[st]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="fila-solicitante" className={label}>Vereador</label>
            <input id="fila-solicitante" className={input} value={solicitante} onChange={(e) => setSolicitante(e.target.value)} placeholder="Nome do solicitante" />
          </div>
          <div>
            <label htmlFor="fila-emenda" className={label}>Emenda</label>
            <input id="fila-emenda" className={input} value={emenda} onChange={(e) => setEmenda(e.target.value)} placeholder="Código da emenda" />
          </div>
          <button type="submit" className={`rounded-xl px-5 py-2 text-sm font-semibold transition ${primary}`}>Filtrar</button>
        </form>
      </section>

      <section className={`rounded-3xl border p-5 ${surface}`}>
        {fila.isLoading && <p className="text-sm text-slate-500 dark:text-slate-400">Carregando...</p>}
        {fila.isError && <p className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar a fila.</p>}
        {fila.data && fila.data.itens.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nenhuma solicitação encontrada.</p>
        )}
        {fila.data && fila.data.itens.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="py-2 pr-4">Protocolo</th>
                  <th className="py-2 pr-4">Solicitante</th>
                  <th className="py-2 pr-4">Refere-se a</th>
                  <th className="py-2 pr-4">Categoria</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Responsável</th>
                  <th className="py-2">Aberta em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {fila.data.itens.map((s) => (
                  <tr key={s.id} className="text-slate-700 dark:text-slate-200">
                    <td className="py-2 pr-4">
                      <Link to={`/dashboard/solicitacoes/${s.id}`} className={`font-semibold hover:underline ${accent}`}>{s.protocolo}</Link>
                    </td>
                    <td className="py-2 pr-4">{s.solicitanteNome}</td>
                    <td className="py-2 pr-4">{rotuloAlvo(s)}</td>
                    <td className="py-2 pr-4">{rotuloCategoria(s.categoria, s.categoriaOutro)}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TOM_STATUS[s.status] ?? ''}`}>
                        {STATUS_SOLICITACAO_LABEL[s.status] ?? s.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4">{s.responsavelNome ?? '—'}</td>
                    <td className="py-2">{formatarData(s.criadaEm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {fila.data && fila.data.total > TAMANHO && (
          <div className="mt-4 flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
            <span>{fila.data.total} solicitações · página {pagina + 1} de {totalPaginas}</span>
            <div className="flex gap-2">
              <button type="button" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40 dark:border-slate-600">Anterior</button>
              <button type="button" disabled={pagina + 1 >= totalPaginas} onClick={() => setPagina((p) => p + 1)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40 dark:border-slate-600">Próxima</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default FilaSolicitacoes;
